type ReplaceState = History['replaceState'];
type PushState = History['pushState'];
type Guarded<T> = T & { __preserveHash?: true };

/**
 * How long the guard stays installed if the refresh never triggers the re-sync
 * write (e.g. nothing to refresh). This is a safety net, not a guarantee: if
 * the App Router's RSC refetch + URL re-sync takes longer than this, the guard
 * is already gone and the hash can still drop. `router.refresh()` exposes no
 * completion signal, so any fixed value is a heuristic; the common case (fast
 * refetch) is well within it. Residual, non-blocking flake risk — see CPS-7492.
 */
const GUARD_TIMEOUT_MS = 2000;

/**
 * Runs a Next.js App Router `refresh()` while preserving the current URL hash.
 *
 * `router.refresh()` makes the App Router re-sync the browser URL to its
 * internal `canonicalUrl` (pathname + search only) via `history.replaceState`.
 * The App Router state does not track the client-side `#…` fragment, so the
 * refresh drops it. On hash-driven pages (the Coveo V1 search / listing pages)
 * the fragment *is* the search state (query + facets), so losing it collapses
 * the page to a bare `/search`. This intermittently broke the "reflect currency
 * change in search results" e2e (a race between the refresh's async
 * `replaceState` and the follow-up navigation) and can lose a real user's
 * query/filters on a currency switch. See CPS-7492.
 *
 * The refresh's URL-sync is asynchronous, so we briefly wrap the history write
 * methods and re-append the fragment to the first write that re-syncs *this*
 * page without one, then restore the originals.
 *
 * The wrapper is deliberately narrow so it can't hijack unrelated writes while
 * active:
 *  - only when the current hash carries serialized state (contains `=`, e.g.
 *    `#q=dogs&nf-ec_price_filter=…`) — bare anchors like `#reviews` are ignored;
 *  - only the write whose target equals the current `pathname + search`;
 *  - it re-appends the *live* fragment (not one captured at call time), so if
 *    the user changed the search state during the window the current state is
 *    preserved, not a stale snapshot;
 *  - it does not stack if a guard is already active (rapid currency changes).
 *
 * App Router 14.2.26 re-syncs via `replaceState`; `pushState` is guarded too so
 * a future switch to `pushState` doesn't silently reintroduce the drop.
 *
 * Known residual (CPS-7492, non-blocking): if the user clears ALL Coveo state
 * inside the guard window, `useUrlManagerSync` writes a hash-less same-page URL
 * that is indistinguishable from the router's own re-sync, so the guard
 * re-appends the pre-clear fragment and reverts the clear. It's narrow (both
 * writes must race inside the sub-second window) and left as-is: distinguishing
 * the two would require coupling this utility to engine state or dropping the
 * `pushState` future-proofing above.
 */
export function refreshPreservingHash(refresh: () => void): void {
  if (typeof window === 'undefined') {
    refresh();
    return;
  }

  const { hash } = window.location;
  if (!hash || !hash.includes('=')) {
    refresh();
    return;
  }

  if ((window.history.replaceState as Guarded<ReplaceState>).__preserveHash) {
    // A guard from a previous (still-pending) refresh is already installed; it
    // will catch this refresh's write too. Don't wrap a wrapper.
    refresh();
    return;
  }

  const originalReplace = window.history.replaceState.bind(window.history);
  const originalPush = window.history.pushState.bind(window.history);
  const samePage = `${window.location.pathname}${window.location.search}`;

  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    window.history.replaceState = originalReplace;
    window.history.pushState = originalPush;
  };

  const guard = <T extends ReplaceState | PushState>(
    original: T
  ): Guarded<T> => {
    const wrapped = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      // The exact string match is load-bearing: Next 14.2.26 re-syncs with a
      // relative, hash-less string. If a future Next re-syncs with a `URL`
      // object or an absolute href this silently becomes a no-op and the
      // hash-drop returns — the currency-switch canary in searchGeneral.spec.ts
      // is the only thing that would catch that on a dependency bump.
      if (url != null && String(url) === samePage) {
        // The App Router re-synced this page's URL without the fragment. Put
        // the *live* fragment back (it may have changed since install), then
        // stop guarding so later writes behave normally.
        const liveHash = window.location.hash;
        if (liveHash && liveHash.includes('=')) {
          original(data, unused, `${samePage}${liveHash}`);
          restore();
          return;
        }
      }
      original(data, unused, url);
    }) as Guarded<T>;
    wrapped.__preserveHash = true;
    return wrapped;
  };

  window.history.replaceState = guard(originalReplace);
  window.history.pushState = guard(originalPush);

  // Schedule the safety net BEFORE refresh() so a synchronously-throwing
  // refresh() can't leave the guard installed forever.
  window.setTimeout(restore, GUARD_TIMEOUT_MS);
  try {
    refresh();
  } catch (err) {
    restore();
    throw err;
  }
}
